import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useLocation } from 'react-router';

import { getRouteTitle, ROUTES } from '../../../constants/RouteNameMapping';
import { useTitle } from '../../../hooks/useTitle';
import { useParseCompanyIdFromPath } from '../../../pages/CompanyProfiles/utils/useParseCompanyIdFromPath';
import { selectedViewPFJ } from '../../../pages/PortfolioOverview/state/ViewStateJ';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { getRouteBasedFinanceTitles } from '../../../pages/Finance2/useRouteBasedFinanceTitles';

const ENTITY_SURFACE_ROUTES = new Set<string>([
  ROUTES.ENTITIES_STRUCTURE_CHART,
  ROUTES.ENTITIES_REGISTRY,
  ROUTES.ENTITIES_DO_SLATE,
  ROUTES.ENTITIES_BENEFICIAL_OWNERSHIP,
  ROUTES.ENTITIES_REVIEW_QUEUES,
]);

export const useBreadCrumbs = () => {
  const location = useLocation();
  const url = location.pathname;
  const label = useTitle();
  const FinTitles = getRouteBasedFinanceTitles();

  const companyProfilesBreadcrumbs = useMemo(() => {
    if (!url.startsWith(`/${ROUTES.COMPANY_PROFILES}`)) {
      return null;
    }
    return [
      {
        label,
      },
      { label: <CompanyNameBreadCrumb /> },
    ];
  }, [url, label]);

  const adminPanelConfigCrumbs = useMemo(() => {
    if (!url.startsWith(`/${ROUTES.ADMIN_PANEL}/${ROUTES.CONFIG}`)) {
      return null;
    }

    const subSections = url.split('/');
    const crumbs = [{ label }];
    if (subSections[2]) {
      crumbs.push({ label: getRouteTitle(subSections[2]) });
    }
    if (subSections[3]) {
      crumbs.push({ label: getRouteTitle(subSections[3]) });
    }
    return crumbs;
  }, [label, url]);

  const integrationsSubSections = useMemo(() => {
    if (!url.startsWith(`/${ROUTES.INTEGRATIONS}`)) {
      return null;
    }
    const subSections = url.split('/');
    // subSections: ['', 'integrations', 'apps', 'appName?']
    const appSegment = subSections[3];

    if (!appSegment) {
      return [{ label: getRouteTitle(ROUTES.INTEGRATIONS) }];
    }

    const appLabel = getRouteTitle(appSegment.toLocaleLowerCase()) ?? appSegment;
    return [
      {
        url: `/${ROUTES.INTEGRATIONS}/${ROUTES.INTEGRATIONS_APPS}`,
        label: getRouteTitle(ROUTES.INTEGRATIONS),
      },
      { label: appLabel },
    ];
  }, [url]);

  const mappedUrl = useMemo(() => {
    const splitUrl = url.split('/')[1];
    if (label === getRouteTitle(ROUTES.COMPANIES)) {
      return `/${ROUTES.COMPANIES}`;
    } else if (splitUrl === ROUTES.FINANCE2) {
      return `${ROUTES.FINANCE2}/${ROUTES.FINANCE2_OVERVIEW}`;
    }

    return `/${splitUrl}`;
  }, [label, url]);

  const lastLabel = useMemo(() => {
    if (url.startsWith('/portfolio')) {
      return <PortfolioBreadCrumb />;
    }

    if (url.startsWith(`/${ROUTES.COMPANY_PROFILES}`)) {
      const paths = url.split('/');

      return getRouteTitle(paths[paths.length - 1]) ?? '';
    }

    if (url.startsWith(`/${ROUTES.ADMIN_PANEL}`)) {
      const subtitle = getRouteTitle(url.split('/')[2]);
      if (subtitle) {
        return subtitle;
      }
    }

    if (url.startsWith(`/${ROUTES.KPI}/${ROUTES.KPI_TEMPLATES}/new`)) {
      return 'New Template';
    }

    if (url.startsWith(`/${ROUTES.DEAL_FLOW}/${ROUTES.DEAL_ANALYTICS}`)) {
      return 'Analytics';
    }

    if (url.startsWith(`/${ROUTES.KPI}/${ROUTES.KPI_REQUESTS}`)) {
      return getRouteTitle(ROUTES.KPI_REQUESTS);
    }

    if (url.startsWith(`/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}`)) {
      return getRouteTitle(ROUTES.KPI_RESPONSES);
    }

    if (url.startsWith(`/${ROUTES.KPI}/${ROUTES.KPI_TEMPLATES}`)) {
      return getRouteTitle(ROUTES.KPI_TEMPLATES);
    }

    if (url.startsWith(`/${ROUTES.KPI}/${ROUTES.KPI_MANAGE}`)) {
      return getRouteTitle(ROUTES.KPI_MANAGE);
    }

    if (url.startsWith(`/${ROUTES.KPI}/${ROUTES.COMPANIES}`)) {
      return 'Companies';
    }

    if (url.startsWith(`/${ROUTES.ENTITIES}`)) {
      // '/entities/registry' -> 'Legal Entity Registry'. Matched against the known
      // surfaces rather than passed straight to getRouteTitle, which echoes back any
      // unrecognised segment ('/entities/typo' would render "typo" as a crumb).
      const surface = url.split('/')[2];
      return getRouteTitle(
        ENTITY_SURFACE_ROUTES.has(surface) ? surface : ROUTES.ENTITIES_STRUCTURE_CHART
      );
    }

    if (url.startsWith(`/${ROUTES.DEAL_FLOW}`)) {
      let lastSection = url.split('/')[3];

      if (lastSection === 'current' || !lastSection) {
        return 'Active Deals';
      } else if (!lastSection.endsWith('ed')) {
        lastSection += 'ed';
      }

      return (
        lastSection.substring(0, 1).toUpperCase() + lastSection.substring(1, lastSection.length) + ' Deals'
      );
    }

    if (url.startsWith(`/${ROUTES.FINANCE2}`)) {
      if (url.includes(ROUTES.FINANCE_RECON)) {
        return FinTitles.recon;
      } else if (url.includes(ROUTES.FINANCE_ALL_SECURITIES)) {
        return FinTitles.securities;
      } else {
        const subSection = url.split('/')[4];
        return getRouteTitle(subSection) ?? FinTitles.main;
      }
    }
    return 'Overview';
  }, [FinTitles.main, FinTitles.recon, FinTitles.securities, url]);

  if (companyProfilesBreadcrumbs) {
    return [];
  }
  if (integrationsSubSections) {
    return [...integrationsSubSections];
  }
  if (adminPanelConfigCrumbs) {
    return [...adminPanelConfigCrumbs];
  }

  if (url.startsWith(`/${ROUTES.DEAL_FLOW}`)) {
    return [{ label }, { label: lastLabel }];
  }
  if (url.startsWith(`/${ROUTES.FUND_PROFILES}`)) {
    return [];
  }
  if (url.startsWith(`/${ROUTES.MULTIPERIOD_REPORTING}`)) {
    return [];
  }

  return [{ url: mappedUrl, label }, { label: lastLabel }];
};

// separate the portfolio breadcrumb component to avoid pulling view data from any module but the Portfolio one
const PortfolioBreadCrumb = () => {
  const view = useAtomValue(selectedViewPFJ)?.label;
  if (!view) {
    return null;
  } else {
    return <>{view}</>;
  }
};

const CompanyNameBreadCrumb = () => {
  const companyId = useParseCompanyIdFromPath();
  const activeCompany = useAtomValue(companyStateJ(companyId));

  return <>{activeCompany?.name ?? ''}</>;
};
